<template>
  <section class="checkout-summary">
    <div
      v-show="showSummary"
      class="checkout-summary__container"
    >
      <div class="checkout-summary__section">
        <button
          class="checkout-summary__toggle checkout-summary__toggle--open"
          @click.prevent="toggleSummary"
        >
          <span class="screen-reader-text">{{ $modeoL10n.close_summary }}</span>
        </button>

        <p class="checkout-summary__heading">
          1. {{ $modeoL10n.your_order }}
          <a
            class="checkout-summary__link"
            href="#tilaus"
          >{{ items == 0 ? $modeoL10n.browse_products : $modeoL10n.edit }}</a>
        </p>
        <div
          class="checkout-summary__text"
          :class="{ 'checkout-summary__text--disabled': items == 0 }"
        >
          <p class="checkout-summary__label">
            {{ $modeoL10n.products_in_total }} <span class="checkout-summary__label-detail">({{ items }})</span>
          </p>
          <p class="checkout-summary__value checkout-summary__value--price">
            <price
              :price-with-tax="subTotalWithTax"
              :price-without-tax="subTotalWithoutTax"
            />
          </p>
        </div>
        <div
          v-if="discount"
          class="checkout-summary__text"
        >
          <p class="checkout-summary__label">
            {{ $modeoL10n.discount }}
          </p>
          <p class="checkout-summary__value checkout-summary__value--price">
            -{{ discount | formatPrice }}
          </p>
        </div>
      </div>

      <div class="checkout-summary__section">
        <p class="checkout-summary__heading">
          2. {{ $modeoL10n.contact_details }}
          <a
            class="checkout-summary__link"
            href="#yhteystiedot"
          >{{ isBillingAddressEmpty? $modeoL10n.fill : $modeoL10n.edit }}</a>
        </p>
        <div
          class="checkout-summary__text"
          :class="{ 'checkout-summary__text--disabled': isBillingAddressEmpty }"
        >
          <p v-if="!isBillingAddressEmpty">
            <template v-if="billingAddress.first_name || billingAddress.last_name">
              {{ billingAddress.first_name }} {{ billingAddress.last_name }}<br>
            </template>
            <template v-if="billingAddress.company">
              {{ billingAddress.company }}<br>
            </template>
            <template v-if="billingAddress.company_id">
              {{ billingAddress.company_id }}<br>
            </template>
            <template v-if="billingAddress.address_1">
              {{ billingAddress.address_1 }},
            </template>
            <template v-if="billingAddress.postcode || billingAddress.city">
              {{ billingAddress.postcode }} {{ billingAddress.city }}<br>
            </template>
            <template v-if="billingAddress.phone">
              {{ billingAddress.phone }}<br>
            </template>
            <template v-if="billingAddress.email">
              {{ billingAddress.email }}
            </template>
          </p>
          <p v-if="isBillingAddressEmpty">
            {{ $modeoL10n.no_contact_details }}
          </p>
        </div>
      </div>

      <div class="checkout-summary__section">
        <p class="checkout-summary__heading">
          3. {{ $modeoL10n.shipping_method }}
          <a
            class="checkout-summary__link"
            href="#toimitustapa"
          >{{ shippingMethod ? $modeoL10n.edit : $modeoL10n.select }}</a>
        </p>
        <div
          class="checkout-summary__text"
          :class="{ 'checkout-summary__text--disabled': !shippingMethod }"
        >
          <p class="checkout-summary__label">
            {{ shippingMethod ? shippingMethod.label : $modeoL10n.no_shipping_method }}
          </p>
          <p
            v-if="shippingMethod"
            class="checkout-summary__value checkout-summary__value--price"
          >
            {{ shippingMethodPrice | formatPrice }}
          </p>
        </div>
      </div>

      <div
        v-if="shippingMethod"
        class="checkout-summary__section"
      >
        <p class="checkout-summary__heading">
          4. {{ $modeoL10n.payment_method }}
          <a
            class="checkout-summary__link"
            href="#maksutapa"
          >{{ $modeoL10n.show }}</a>
        </p>
        <div class="checkout-summary__text checkout-summary__text">
          {{ paymentMethod.label }}
        </div>
      </div>

      <div class="checkout-summary__section">
        <div class="checkout-summary__text">
          <p class="checkout-summary__label">
            {{ $modeoL10n.subtotal }}
          </p>
          <!-- don't use <price> here -->
          <p class="checkout-summary__value checkout-summary__value--price">
            {{ subTotalWithoutTaxIncludingShipping | formatPrice }}
          </p>
        </div>
        <div class="checkout-summary__text">
          <p class="checkout-summary__label">
            {{ $modeoL10n.vat }}
          </p>
          <!-- don't use <price> here -->
          <p class="checkout-summary__value checkout-summary__value--price">
            {{ totalTax | formatPrice }}
          </p>
        </div>
        <div class="checkout-summary__total">
          <p class="checkout-summary__label">
            {{ $modeoL10n.total }}
          </p>
          <!-- don't use <price> here -->
          <p class="checkout-summary__total__value checkout-summary__value checkout-summary__value--price">
            {{ totalPrice | formatPrice }}
          </p>
        </div>
      </div>
    </div>

    <div
      v-if="!showSummary"
      class="checkout-summary__mobile-preview"
    >
      <button
        class="checkout-summary__toggle"
        @click.prevent="toggleSummary"
      >
        <span class="screen-reader-text">{{ $modeoL10n.show_summary }}</span>
      </button>
      <div class="checkout-summary__total checkout-summary__total--mobile">
        <p class="checkout-summary__label">
          {{ $modeoL10n.total }}
        </p>
        <!-- don't use <price> here -->
        <p class="checkout-summary__total__value checkout-summary__value checkout-summary__value--price">
          {{ totalPrice | formatPrice }}
        </p>
      </div>
    </div>
  </section>
</template>

<script>
    import _ from 'lodash';

    export default {
        props: {
            defaultShippingMethod: {
                type: Object,
                default: function() {
                    return {};
                },
            },
        },
        data : function () {
            return {
                showSummary: false,
            };
        },
        computed: {
            items: function() {
                return this.$store.getters.getTotals.count;
            },
            subTotalWithTax: function() {
                const totals = this.$store.getters.getTotals;
                return this.parseCommaDecimalFormat( totals.subtotal ) + this.parseCommaDecimalFormat( totals.subtotalTax );
            },
            subTotalWithoutTax: function() {
                const totals = this.$store.getters.getTotals;
                return this.parseCommaDecimalFormat( totals.subtotal )
            },
            subtotal: function() {
                const showPricesWithVat = this.$store.getters.showPricesWithVat;
                return (showPricesWithVat) ? this.subTotalWithTax : this.subTotalWithoutTax;
            },
            subTotalWithoutTaxIncludingShipping: function() {
                const totals = this.$store.getters.getTotals;

                if (this.shippingMethod.cost) {
                    return this.parseCommaDecimalFormat( totals.subtotal - totals.discount ) + this.parseCommaDecimalFormat(this.shippingMethod.cost)
                } else {
                    return this.parseCommaDecimalFormat( totals.subtotal - totals.discount );
                }
            },
            discount: function() {
                const showPricesWithVat = this.$store.getters.showPricesWithVat;
                if (showPricesWithVat) {
                  return parseFloat(this.$store.getters.getTotals.discount + this.$store.getters.getTotals.discountTax);
                } else {
                  return parseFloat(this.$store.getters.getTotals.discount);
                }
            },
            totalPrice: function() {
                return this.$store.getters.getTotalIncludingShipping;
            },
            totalTax: function() {
                return this.$store.getters.getTotalIncludingShipping - this.$store.getters.getTotalIncludingShippingWithoutTax;
            },
            shippingMethodPrice() {
                const showPricesWithVat = this.$store.getters.showPricesWithVat;
                if ( this.shippingMethod ) {
                    return (!showPricesWithVat) ? this.shippingMethod.cost : this.shippingMethod.cost_with_tax;
                } else {
                    return false;
                }
            },
            shippingMethod() {
                if (typeof this.$store.getters.getShippingMethod !== 'undefined') {
                    return this.$store.getters.getShippingMethod;
                } else {
                    return false;
                }
            },
            billingAddress() {
                if (typeof this.$store.getters.getBillingAddress !== 'undefined') {
                    return this.$store.getters.getBillingAddress.address;
                } else {
                    return false;
                }
            },
            isBillingAddressEmpty() {
                return _.isEmpty(this.billingAddress);
            },
            paymentMethod() {
                if (typeof this.$store.getters.getPaymentMethod !== 'undefined') {
                    return this.$store.getters.getPaymentMethod;
                } else {
                    return false;
                }
            },
        },
        created: function () {
            if (!_.isEmpty( this.defaultShippingMethod ) && typeof this.$store.getters.getShippingMethod === 'undefined') {
                this.$store.commit( 'setShippingMethod', this.defaultShippingMethod );
            }
        },

        methods: {
            setPaymentMethod : function(method) {
                this.paymentMethod = method;
            },

            toggleSummary() {
                this.showSummary = !this.showSummary;
            },
        },
    }
</script>
